import type { Role } from '../api/schemas';

/**
 * The low-level session record: a plain module, not a React context, so the
 * API client (`src/api/client.ts`) can read the current transport/token
 * without importing React. `SessionContext` owns the React-facing state and
 * calls into this module to persist it.
 *
 * Cookie-transport sessions carry no token (the browser holds the cookie),
 * but the non-secret identity fields are still persisted so a reload can
 * restore role-gating and business scoping before the first request confirms
 * the cookie is still valid. If it isn't, the first 401 clears this store.
 */

const STORAGE_KEY = 'dale-business-session';

export type SessionTransport = 'cookie' | 'bearer';

export type StoredSession = {
  transport: SessionTransport;
  /** Present only for bearer sessions; a cookie session has no client-held secret. */
  token?: string;
  businessUserId: string;
  businessId: string;
  role: Role;
  /** Set on a coach login: the roster entry whose classes and bookings they see. */
  staffMemberId?: string | null;
  expiresAt: string;
  /** True on first login or after a titular resets the password; blocks the whole panel until resolved. */
  mustChangePassword?: boolean;
  /** A new signup whose card Stripe has not confirmed and whose trial is over: the panel stays on the card step (/pago). */
  cardRequired?: boolean;
  /** When that lock starts (the free trial's end) while the card is pending; null when nothing will lock. */
  trialEndsAt?: string | null;
};

let current: StoredSession | null = null;

export function getSession(): StoredSession | null {
  return current;
}

export function setSession(session: StoredSession): void {
  current = session;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  current = null;
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Flips `mustChangePassword` off after a successful password change, without
 * mutating the previous session record in place. Returns the updated session
 * (or null if there was none to update, which should not happen in practice
 * since this only runs after an authenticated mutation succeeds).
 */
export function markPasswordChanged(): StoredSession | null {
  if (!current) return null;
  const next: StoredSession = { ...current, mustChangePassword: false };
  setSession(next);
  return next;
}

/**
 * Flips `cardRequired` off and stops the trial countdown once Stripe
 * confirmed the card, without mutating the previous session record in place.
 * Returns the updated session, or null when there is none.
 */
export function markCardConfirmed(): StoredSession | null {
  if (!current) return null;
  const next: StoredSession = { ...current, cardRequired: false, trialEndsAt: null };
  setSession(next);
  return next;
}

/**
 * Moves the stored session onto a different location of the same
 * organization after a successful `POST /v1/business/session/location`,
 * without mutating the previous session record in place.
 */
export function setSessionBusinessId(businessId: string): StoredSession | null {
  if (!current) return null;
  const next: StoredSession = { ...current, businessId };
  setSession(next);
  return next;
}

/** Hydrates the in-memory session from sessionStorage; drops an expired one. */
export function restoreSession(): StoredSession | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  let parsed: StoredSession;
  try {
    parsed = JSON.parse(raw) as StoredSession;
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }

  if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }

  current = parsed;
  return parsed;
}
